import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { ConfigService } from '../../core/services/config.service';
import { MerchantStore } from '../../core/models/merchant-store.model';
import { MapboxMapComponent } from '../../checkout/checkout-enjoy/map-tracker/mapbox-map/mapbox-map.component';
import { LatLng } from '../../checkout/checkout-enjoy/map-tracker/models/lat-lng';
import { MapsService } from '../../checkout/checkout-enjoy/map-tracker/maps.service';
import { NamedMapElement } from '../../checkout/checkout-enjoy/map-tracker/models/named-map-element';
import { IconComponent } from '../icons/icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { UseMyLocationButtonComponent } from '../use-my-location-button/use-my-location-button.component';
import { Observable, Subscription } from 'rxjs';
import { LayoutService } from '../../core/services/layout.service';
import { CommonModule } from '@angular/common';
import { AddressService } from '../../core/services/address.service';
import { OrderService } from '../../core/services/order.service';
import { ReloaderService } from '../../core/services/reloader.service';
import { InitParamsService } from '../../core/services/init-params.service';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { AuthService } from '../../core/services/auth.service';
import { Account } from '../../core/models/account/account.model';
import { AsyncButtonDirective } from '../directives/async-button.directive';
import { AuditService } from '../../core/services/audit.service';

@Component({
  selector: 'cwo-store-chooser',
  standalone: true,
  imports: [CommonModule,
    MapboxMapComponent,
    IconComponent,
    FormsModule,
    TranslocoDirective,
    UseMyLocationButtonComponent,
    CheckboxComponent,
    AsyncButtonDirective
  ],
  templateUrl: './store-chooser.component.html',
  styleUrl: './store-chooser.component.scss',
})

export class StoreChooserComponent {
  configService = inject(ConfigService);
  mapsService = inject(MapsService);
  layoutService = inject(LayoutService);
  addressService = inject(AddressService);
  orderService = inject(OrderService); 
  translocoService = inject(TranslocoService);
  reloaderService = inject(ReloaderService);
  authService = inject(AuthService);
  auditService = inject(AuditService);
  
  stores: MerchantStore[] = [];
  mapCenter: LatLng = { lat: 0, lng: 0 };
  mapElements: NamedMapElement[] = [];
  currentStore: MerchantStore | undefined = undefined;
  selectedStore: MerchantStore | undefined = undefined;
  iconNameType = IconNameType;
  searchInput = '';
  searchStoresResult: MerchantStore[] = [];
  searchTriggered = false;
  storesLoading = false;
  getAddressSubscription: Subscription | null = null;
  noResultsTitle = '';
  noResultsPrompt = '';
  initParamsService = inject(InitParamsService);
  locationError = '';
  shouldShowMakeFavoriteStore: boolean = false;
  saveForFutureOrders: boolean = false;
  loadingSubscription: Subscription | null = null;
  searchQueryUsed = '';
  isMobile = signal(false);

  account = computed(() => {
    return this.authService.state().account;
  });

  readonly mapboxMapComponent = viewChild(MapboxMapComponent);

  constructor() {
    if (this.initParamsService.isEmbedded) this.isMobile.set(false);
    else this.isMobile.set(this.layoutService.mobileView());
    this.configService.fetchMerchantStores().subscribe((s) => {
      this.currentStore = s.find((store) => store.isCurrentStore);
      this.stores = s.filter((store) => !store.isCurrentStore);
      this.shouldShowMakeFavoriteStore = !!(this.account() && this.account()?.isInstantiated && this.account()?.favoriteStore !== this.currentStore?.primaryKey);
      this.mapCenter = this.getLatLng(this.stores[0]);
      this.setMapElements(this.getOrderedStores());
    });
  }

  setMapElements(stores: MerchantStore[]) {
    this.mapElements = [];
    stores.forEach((store, index) => {
      this.mapElements = this.mapsService.upsertElement(this.mapElements, {
        name: store.primaryKey,
        coordinates: this.getLatLng(store),
        index: index + 1,
      });
    });
  }
  
  selectStore(store: MerchantStore) {
    this.selectedStore = store;
    this.shouldShowMakeFavoriteStore = !!(this.account() && this.account()?.isInstantiated && this.account()?.favoriteStore !== this.selectedStore?.primaryKey);
    setTimeout(() => {
      this.mapboxMapComponent()?.map?.resize();
      this.mapboxMapComponent()?.focusOnMarker(this.getLatLng(store));
      this.mapboxMapComponent()?.changeMarkerColor(store.primaryKey, 'var(--primary-inverse)', 'var(--primary)', 'var(--primary)');
    },0);
    this.auditService.createAudit(() => `User chose store ${store.displayName}`);
    
  }

  private setMyFavoriteStore(favoriteStore: string): Observable<Account> {
    const accountCopy = new Account().deserialize({ ...this.account(), favoriteStore });
    return this.authService.updateFavoriteStore(accountCopy);
  }

  backToStoresList() {
    this.mapboxMapComponent()?.changeMarkerColor(this.selectedStore?.primaryKey ?? '', 'var(--primary)', 'var(--primary-inverse)', 'var(--primary)');
    this.selectedStore = undefined;
    this.resetMapViewWithElements();
    
  }

  goToStore(store: MerchantStore) {
    if (!this.saveForFutureOrders) {
      this.auditService.createAudit(() => `Go to store button clicked. User clicked to change from ${this.currentStore?.displayName} to ${store.displayName}`)
      this.reloaderService.redirect(
        `/ws/integrated/v1/store/${encodeURIComponent(store.primaryKey)}/url`,
        {
          orderType: this.orderService.order()?.orderType
        }
      );
    } else {
      this.loadingSubscription = this.setMyFavoriteStore(store.primaryKey).subscribe({
        complete: () => {
          this.loadingSubscription = null;
          this.auditService.createAudit(() => `Go to store button clicked. User clicked to change from ${this.currentStore?.displayName} to ${store.displayName}`)
          this.reloaderService.redirect(
            `/ws/integrated/v1/store/${encodeURIComponent(store.primaryKey)}/url`,
            {
              orderType: this.orderService.order()?.orderType
            }
          );
        }
      });
    }
  }

  searchStoresByAddress() {
    if(!this.searchInput) return;
    this.searchQueryUsed = this.searchInput;
    this.fetchStores(this.searchInput);
  }

  clearSearch() {
    if(this.searchTriggered) {
      this.searchQueryUsed = '';
      this.searchTriggered = false;
      this.mapboxMapComponent()?.clearAllMapElements();
      const allStores = this.getOrderedStores();
      this.resetMapViewWithElements(allStores);
    }
    this.searchInput = '';
  }

  handleMarkerClicked(marker: NamedMapElement) {
    if(this.selectedStore) this.mapboxMapComponent()?.changeMarkerColor(this.selectedStore?.primaryKey, 'var(--primary)', 'var(--primary-inverse)', 'var(--primary)');
    const store = this.stores.find((s) => s.primaryKey === marker.name);
    if (store && store.primaryKey !== this.currentStore?.primaryKey) {
      this.selectStore(store);
    }
  }
  location = '';

  searchStoresByLocation() {
    this.locationError = '';
    this.setNoResultsViewLabels();
    this.searchInput = '';
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.getAddressSubscription = this.addressService.findAddress(position.coords.latitude.toString(), position.coords.longitude.toString()).subscribe({
            next: (res) => {
              this.location = res.address;
                const searchAddress = `${res.address} ${res.city} ${res.state} ${res.zip}`;
                this.searchInput = searchAddress;
                this.searchQueryUsed = searchAddress;
                this.fetchStores(searchAddress);
                this.getAddressSubscription = null;        
                this.searchTriggered = true; 
            },
            error: () => {
              this.setNoResultsViewLabels('location-error');
              this.searchStoresResult = [];
              this.resetMapViewWithElements(this.getOrderedStores());
              this.getAddressSubscription = null;
              this.searchTriggered = true;
            }
          });
        },
        (error: GeolocationPositionError) => {
          this.setNoResultsViewLabels('location-error');
          this.searchStoresResult = [];
          this.resetMapViewWithElements(this.getOrderedStores());
          this.getAddressSubscription = null;
          this.locationError = error.message;
        }
      );
    }
  }

  fetchStores(address: string){
    this.storesLoading = true;
    this.configService
      .fetchGeocodeStores(address)
      .subscribe((stores) => {
        this.searchTriggered = true;
        this.searchStoresResult = stores;
        this.mapboxMapComponent()?.clearAllMapElements();
        this.auditService.createAudit(() => `Searching stores button clicked. Stores found: ${this.searchStoresResult}`)
        this.searchStoresResult.forEach((store) => {
          if(store.primaryKey === this.currentStore?.primaryKey) {
            store.isCurrentStore = true;
          }
        })
        if (stores.length) {
          this.setNoResultsViewLabels();
          this.setMapElements(this.searchStoresResult);
        } else {
          this.setMapElements(this.getOrderedStores());
          this.setNoResultsViewLabels('no-results');
        }
        if(stores.length === 1 && stores[0].primaryKey === this.currentStore?.primaryKey) {
          this.setNoResultsViewLabels('only-current-store');
        }
        this.storesLoading = false; 
        this.selectedStore = undefined;
        this.resetMapViewWithElements();
      });
  }

  setNoResultsViewLabels(useCase?: 'no-results' | 'only-current-store' | 'location-error') {
    switch(useCase) {
      case 'only-current-store': {
        this.noResultsTitle = this.translocoService.translate('shared_components.store_chooser.already_ordering_nearest_store');
        this.noResultsPrompt = this.translocoService.translate('shared_components.store_chooser.already_ordering_nearest_store_prompt');
       break; 
      }
      case 'location-error': {
        this.noResultsTitle = this.translocoService.translate('shared_components.store_chooser.no_stores_found_near');
        this.noResultsPrompt = this.translocoService.translate('shared_components.store_chooser.no_stores_found_near_prompt');
        break;
      }
      case 'no-results': {
        this.noResultsTitle = this.translocoService.translate('shared_components.store_chooser.no_stores_found');
        this.noResultsPrompt = this.translocoService.translate('shared_components.store_chooser.no_stores_found_prompt');
        break;
      }
      default: {
        this.noResultsTitle = '';
        this.noResultsPrompt = '';
      }
    }
  }

  private resetMapViewWithElements(stores?: MerchantStore[]) {
    if(stores) this.setMapElements(stores);
    setTimeout(() => {
      this.mapboxMapComponent()?.map?.resize();
      this.mapboxMapComponent()?.setMapView();
    }, 0);
  }

  private getOrderedStores(): MerchantStore[] {
    return [this.currentStore, ...this.stores].filter((s) => s !== undefined) as MerchantStore[];
  }

  private getLatLng(store: MerchantStore): LatLng {
    return {
      lat: Number(store.latitude) || 0,
      lng: Number(store.longitude) || 0,
    };
  }
}
